import { Link } from "wouter";
import { ArrowRight, Check, MapPin, Clock, Truck, Shield, Zap, Target } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead, { createLocalBusinessSchema } from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES, CONTACT } from "@/lib/constants";
import LazyImage from "@/components/LazyImage";

const localBenefits = [
  {
    icon: Truck,
    title: "Snabb leverans till Södertälje",
    text: "Vi planerar leveransen från Eskilstuna till Södertälje utifrån detaljernas omfattning, önskat datum och vald transportlösning.",
  },
  {
    icon: Clock,
    title: "Korta ledtider för industrin",
    text: "Tack vare tre CO2-lasrar och hög kapacitet kan vi hantera brådskande beställningar från Södertäljes industrier med kort varsel.",
  },
  {
    icon: Shield,
    title: "Garanterad precision",
    text: "Toleranser ner till 0,1 mm med moderna Trumpf- och Amada CO2-lasrar. Första bit-kontroll vid varje projektstart.",
  },
  {
    icon: Target,
    title: "Helhetslösning",
    text: "Kombinera laserskärning med kantpressning och svetsning – allt under ett tak. Färdiga komponenter levererade till Södertälje.",
  },
];

const sodertaljeIndustries = [
  {
    name: "Fordonsindustri",
    desc: "Vi tillverkar precisionskomponenter för fordonsindustrin och dess underleverantörer i Södertälje.",
  },
  {
    name: "Läkemedel & Forskning",
    desc: "Specialanpassade komponenter i rostfritt stål för läkemedelsindustrin och dess forsknings- och produktionsanläggningar.",
  },
  {
    name: "Logistik & Transport",
    desc: "Detaljer för transportutrustning, lagerinredning och automationslösningar för Södertäljes logistiknav.",
  },
  {
    name: "Tillverkningsindustri",
    desc: "Prototyper och serieproduktion för tillverkande företag i Södertälje. Från enstaka detaljer till stora volymer.",
  },
];

const capabilities = [
  { material: "Svart stål", maxThickness: "25 mm", minThickness: "0,5 mm" },
  { material: "Rostfritt stål", maxThickness: "20 mm", minThickness: "0,5 mm" },
  { material: "Aluminium", maxThickness: "15 mm", minThickness: "0,5 mm" },
  { material: "Höghållfasthetsstål (Strenx)", maxThickness: "20 mm", minThickness: "1 mm" },
  { material: "Slitstål (Hardox/Raex)", maxThickness: "20 mm", minThickness: "1 mm" },
];

export default function LaserskarningSodertalje() {
  const localServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Laserskärning Södertälje",
    description:
      "Professionell laserskärning för företag i Södertälje. ASPAB erbjuder CO2 Laser-skärning med toleranser ner till 0,1 mm, snabba leveranser och brett materialutbud.",
    provider: {
      "@type": "LocalBusiness",
      name: "ASPAB",
      telephone: "+46161326 00",
      email: "order@aspab.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Abramsons väg 2",
        addressLocality: "Eskilstuna",
        postalCode: "632 39",
        addressCountry: "SE",
      },
    },
    areaServed: {
      "@type": "City",
      name: "Södertälje",
      containedInPlace: {
        "@type": "Country",
        name: "Sweden",
      },
    },
    serviceType: "Laserskärning",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Laserskärningstjänster",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning stål",
            description: "Laserskärning i svart stål upp till 25 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning rostfritt",
            description: "Laserskärning i rostfritt stål upp till 20 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning aluminium",
            description: "Laserskärning i aluminium upp till 15 mm tjocklek",
          },
        },
      ],
    },
  };

  const localBusinessSchema = createLocalBusinessSchema({
    areaServed: "Södertälje",
    serviceDescription: "Professionell laserskärning, kantpressning och svetsning för företag i Södertälje. Leveransplan bekräftas i offerten.",
  });

  return (
    <Layout>
      <SEOHead
        title="Laserskärning Södertälje – för industriföretag | ASPAB"
        description="Laserskärning för Södertäljes fordons-, läkemedels- och tillverkningsindustri. Toleranser ner till 0,1 mm. Begär offert från ASPAB."
        canonical="https://aspab.com/laserskarning-sodertalje"
        path="/laserskarning-sodertalje"
        keywords="laserskärning Södertälje, plåtbearbetning Södertälje, CO2-laser Södertälje, metallbearbetning Södertälje"
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Södertälje", href: "/laserskarning-sodertalje" },
        ]}
        structuredData={[localServiceSchema, localBusinessSchema]}
        ogImage={IMAGES.ogStockholm}      />

      <PageHero
        title="Laserskärning Södertälje"
        subtitle="Specialiserad laserskärning för Södertäljes industri, från fordon till pharma. Precision, kvalitet och snabbhet från ASPAB i Eskilstuna."
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Södertälje", href: "/laserskarning-sodertalje" },
        ]}
        image={IMAGES.heroLaser}
      />

      {/* Intro – Varför välja ASPAB från Södertälje */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Laserskärning för Södertälje
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Din partner för laserskärning i Södertälje
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Behöver ditt företag i Södertälje en pålitlig leverantör av laserskurna detaljer? ASPAB i
                  Eskilstuna erbjuder snabb och precis laserskärning med leverans till hela Södertälje-regionen.
                  Med planerade transporter från Eskilstuna får du en smidig leveranslösning och kapaciteten hos en specialiserad laserskärningsverkstad.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Vi har erfarenhet av kravbilden inom fordonsindustrin och dess leverantörskedjor.
                  Oavsett om du behöver prototyper för R&D, komponenter till läkemedelsindustrin eller stora serier
                  levererar vi med precision och korta ledtider.
                </p>
                <div className="flex items-center gap-3 mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <MapPin className="w-5 h-5 text-[#2563EB] shrink-0" />
                  <div>
                    <span className="text-gray-900 font-medium text-sm">Leverans från ASPAB i Eskilstuna till Södertälje</span>
                    <span className="text-gray-500 text-sm block">Via E20 – snabb och smidig leverans</span>
                  </div>
                </div>
                <Link
                  href="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors mt-6"
                >
                  Begär offert – fri leveransbedömning <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="space-y-4">
                <LazyImage
                  src={IMAGES.laserOldSite}
                  alt="Laserskärning för företag i Södertälje – ASPAB CO2 Laser"
                  className="w-full rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">0,1 mm</div>
                    <div className="text-gray-500 text-sm mt-1">Tolerans</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">&lt;24h</div>
                    <div className="text-gray-500 text-sm mt-1">Leverans enkla detaljer</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">25 mm</div>
                    <div className="text-gray-500 text-sm mt-1">Max tjocklek stål</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">30+</div>
                    <div className="text-gray-500 text-sm mt-1">Års erfarenhet</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Fördelar med ASPAB för Södertälje */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Fördelar
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Varför Södertäljes industri väljer ASPAB
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Närhet, kapacitet och specialistkunskap – vi kombinerar fördelarna med en lokal partner med musklerna hos en specialiserad produktionsanläggning.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {localBenefits.map((item, i) => (
              <AnimatedSection delay={i * 100} key={item.title}>
                <div className="bg-white p-6 rounded-lg border border-gray-200 h-full">
                  <item.icon className="w-8 h-8 text-[#2563EB]" />
                  <h3 className="font-heading text-lg font-bold text-gray-900 mt-4">{item.title}</h3>
                  <p className="text-gray-500 mt-2 text-sm">{item.text}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Industrier i Södertälje */}
      <section className="bg-white py-20 md:py-28 border-t">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Lokala industrier
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Laserskärning för Södertäljes nyckelindustrier
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  ASPAB erbjuder produktionskapacitet för Södertäljes fordons-, läkemedels- och tillverkningsindustri. Vi förstår de höga kraven på precision, materialkvalitet och leveranssäkerhet.
                </p>
                <div className="space-y-4 mt-8">
                  {sodertaljeIndustries.map((industry) => (
                    <div key={industry.name} className="flex items-start gap-4">
                      <div className="bg-[#2563EB] text-white rounded-full p-1.5 mt-1 shrink-0">
                        <Check className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{industry.name}</h4>
                        <p className="text-gray-500 text-sm">{industry.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <LazyImage
                src={IMAGES.workshop1}
                alt="Laserskärning för fordonsindustrin i Södertälje"
                className="w-full rounded-lg"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Material & kapacitet */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <LazyImage
                src={IMAGES.detail}
                alt="CO2-laserskärning i stål, rostfritt och aluminium"
                className="w-full rounded-lg"
              />
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Kapacitet
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  CO2-lasrar för alla material
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Vår maskinpark består av tre moderna CO2-laserskärmaskiner från Trumpf och Amada. CO2-lasrar är väl lämpade för stabil skärning och hög snittkvalitet i grövre material, vilket är viktigt i många industriella tillämpningar.
                </p>
                <div className="overflow-x-auto mt-6">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Material</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Max tjocklek</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Minsta tjocklek</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {capabilities.map((cap) => (
                        <tr key={cap.material}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{cap.material}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cap.maxThickness}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cap.minThickness}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-3">Kontakta oss för andra material som mässing, koppar eller titan.</p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20 md:py-28 border-t">
        <div className="container text-center max-w-3xl mx-auto">
          <AnimatedSection>
            <Zap className="w-12 h-12 text-[#2563EB] mx-auto" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-6">
              Redo att starta ditt projekt i Södertälje?
            </h2>
            <p className="text-gray-500 mt-4 text-lg leading-relaxed">
              Skicka in din förfrågan så återkommer vi med en kostnadsfri offert och leveransplan anpassad för ditt företag i Södertälje. Vårt team är redo att hjälpa dig med allt från materialval till logistik.
            </p>
            <Link
              href="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors mt-8 text-lg"
            >
              Begär offert nu <ArrowRight className="w-5 h-5" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
